import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import { customization, dialogs, globals, i18next, template } from '@yola/ws-sdk';
import upsell from 'src/js/modules/upsell';
import segment from 'src/js/modules/analytics/segment';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common.js';
import constants from '../constants';
import helpers from '../helpers';
import readOnlyModeDecorator from '../helpers/read-only-mode-decorator';
import DesignButtonsDialog from '../components/design-buttons-dialog';
import dialogTypes from '../../dialogs/constants/dialog-types.js';

const { getSelectorByGlobalId, setCurrentButtonPreset, prepareNodeElement, sortPresets } = helpers;

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const {
  hostingPackageFeatureNames: { PREMIUM_BUTTON_PRESETS },
} = subscriptionManagerConstants;

const { GLOBAL_HEADER_CODE } = constants;

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Buttons')],
  title: i18next.t('Select a button preset'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
  description: i18next.t(
    'Choosing a button preset updates all buttons on your site for a consistent look and feel'
  ),
  presetLabel: i18next.t('Preset'),
  presetButtonLabel: i18next.t('Button'),
  editLabel: i18next.t('Settings'),
  premiumLabel: i18next.t('PRO'),
});

const DesignButtonsContainer = ({
  sourceId,
  activePresetId: initiallyActivePresetId = null,
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
}) => {
  const { offsetX, offsetY } = getSharedData();
  const dispatch = useDispatch();

  const currentButtonPreset = useSelector(customization.accessors.getCurrentButtonPreset);
  const currentColorPalette = useSelector(customization.accessors.getCurrentColorPalette);
  const currentFontPair = useSelector(customization.accessors.getCurrentFontPair);

  const buttonPresets = useSelector(customization.selectors.getButtonPresets);
  const buttonPresetVariable = useSelector((state) =>
    template.selectors.getVariableValueByPath(state, customization.constants.BUTTON_PRESET_VARIABLE)
  );
  const buttonPresetTemplate = useSelector(customization.selectors.getButtonPresetTemplate);

  const initialButtonPresetId = currentButtonPreset?.id;
  const initialColorPaletteId = currentColorPalette?.id;
  const initialFontPairId = currentFontPair?.id;

  const [activeButtonPresetId, setActiveButtonPresetId] = useState(
    initiallyActivePresetId || initialButtonPresetId
  );
  const [dialogPosition, updateDialogPosition] = useState({
    offsetX,
    offsetY,
  });

  const captions = getCaptions();

  const sortedPresets = useMemo(
    () => sortPresets(buttonPresets, initiallyActivePresetId || initialButtonPresetId),
    [buttonPresets, initiallyActivePresetId, initialButtonPresetId]
  );

  const handleDragEnd = (_, { x, y }) => {
    updateDialogPosition({ offsetX: x, offsetY: y });
  };

  const applyButtonPreset = useCallback(
    (buttonPresetId) => {
      const { element, compiledElement: buttonPresetElement } = prepareNodeElement({
        elementVariable: buttonPresetVariable,
        customTemplate: buttonPresetTemplate,
        config: buttonPresets.find((preset) => preset.id === buttonPresetId),
        elementDataAttribute: customization.constants.BUTTON_PRESET_DATA_ATTRIBUTE,
      });

      if (element) {
        element.parentNode.replaceChild(buttonPresetElement, element);
      }
    },
    [buttonPresetTemplate, buttonPresetVariable, buttonPresets]
  );

  const handleButtonPresetChange = (buttonPresetId, isPremiumButtonPreset) => {
    if (isPremiumButtonPreset) {
      upsell.operations
        .requestFeatureAvailability(PREMIUM_BUTTON_PRESETS)
        .then(() => {
          setActiveButtonPresetId(buttonPresetId);
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.log('Upgrade flow was canceled');
        });
    } else {
      setActiveButtonPresetId(buttonPresetId);
    }

    trackEvent(events.DESIGN_SETTINGS_BUTTON_PRESET_CLICKED, {
      buttonPreset: buttonPresetId,
    });
  };

  const updateSharedData = (updatedData = {}) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      ...updatedData,
    });
  };

  const handlePresetEdit = (presetId) => {
    updateSharedData();
    applyButtonPreset(presetId);
    dispatch(
      dialogs.actions.show(dialogTypes.BUTTON_PRESET_SETTINGS, { presetId, activeButtonPresetId })
    );
  };

  const onClose = () => {
    dispatch(dialogs.actions.hide());
  };

  const handleCancel = () => {
    if (initialButtonPresetId !== activeButtonPresetId) {
      applyButtonPreset(initialButtonPresetId);
    }

    onDialogCancel({
      sourceId,
    });
    onClose();
  };

  const handleSubmit = (event) => {
    const isButtonPresetChanged = initialButtonPresetId !== activeButtonPresetId;

    if (isButtonPresetChanged) {
      globals.operations.changeGlobalContent(
        GLOBAL_HEADER_CODE,
        (global) => {
          const transformations = [];

          const buttonPreset = sortedPresets.find((preset) => preset.id === activeButtonPresetId);
          const buttonPresetSelector = getSelectorByGlobalId(
            buttonPresetVariable,
            GLOBAL_HEADER_CODE
          );
          const setCurrentButtonPresetCurry = (globalItem) =>
            setCurrentButtonPreset(
              globalItem,
              buttonPreset,
              buttonPresetTemplate,
              buttonPresetSelector
            );
          transformations.push(setCurrentButtonPresetCurry);

          return compose(...transformations)(global);
        },
        false
      );
    }

    onDialogMainAction({
      triggerId: !event ? DIALOG_BACKGROUND_TRIGGER_ID : SUBMIT_BUTTON_TRIGGER_ID,
      sourceId,
      buttonPresetAdjusted: isButtonPresetChanged,
      ...(isButtonPresetChanged && { buttonPreset: activeButtonPresetId }),
    });
    onClose();
  };

  useEffect(() => {
    if (activeButtonPresetId) {
      applyButtonPreset(activeButtonPresetId);
    }
  }, [activeButtonPresetId, applyButtonPreset]);

  return (
    <DesignButtonsDialog
      captions={captions}
      offsetX={offsetX}
      offsetY={offsetY}
      presets={sortedPresets}
      activePaletteId={initialColorPaletteId}
      activeFontId={initialFontPairId}
      activePresetId={activeButtonPresetId}
      onChange={readOnlyModeDecorator(handleButtonPresetChange)}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onEdit={readOnlyModeDecorator(handlePresetEdit)}
      onDragEnd={handleDragEnd}
    />
  );
};

DesignButtonsContainer.propTypes = {
  sourceId: PropTypes.string.isRequired,
  activePresetId: PropTypes.string,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default DesignButtonsContainer;
